import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { markActivity } from "@/components/InstallPrompt";
import { Brand } from "@/components/Logo";
import DictateButton from "@/components/DictateButton";
import { SendHorizontal, Sparkles, Globe, ExternalLink, Copy, Check, Share2, ThumbsUp, ThumbsDown, Eraser, FileText, ArrowRight, Plus, Camera, Image as ImageIcon, X, Loader2, Paperclip, Wand2, GitCompare, Layers, Download, ImageDown, RefreshCw } from "lucide-react";

const PROMPTS_FALLBACK = [];

const EDIT_PRESETS = [
  { label: "Remove background", instr: "Remove the background from this image and make it transparent/clean.", icon: Wand2 },
  { label: "Brighten", instr: "Make it brighter and improve the lighting and exposure.", icon: Sparkles },
  { label: "Restore old photo", instr: "Restore this old photo: fix scratches and damage, reduce noise, and improve clarity while keeping it natural.", icon: Wand2 },
  { label: "Enhance", instr: "Enhance this photo: sharpen the details and improve the overall quality.", icon: Sparkles },
];
const MULTI_PRESETS = [
  { label: "Compare", instr: "Compare these images and describe the key similarities and differences.", icon: GitCompare },
  { label: "Combine", instr: "Combine these images into a single cohesive image.", icon: Layers },
];

const abs = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function renderText(text) {
  const esc = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const withBold = esc.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const html = withBold
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
    .join("");
  return { __html: html };
}

export default function ChatView({ conversationId, isPro, usage, onNewChat, onConversationCreated, onTitleMaybeChanged, onUsageChanged, onLimitReached }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [ratings, setRatings] = useState({});
  const [dlIdx, setDlIdx] = useState("");
  const [savedImgs, setSavedImgs] = useState({});
  const [savingImg, setSavingImg] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const camRef = useRef(null);
  const photoRef = useRef(null);
  const docRef = useRef(null);
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const navigate = useNavigate();
  const skipReloadRef = useRef(false);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setAttachOpen(false);
    setUploading(true);
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name} is over 20MB`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, data]);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Couldn't upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const ATTACH_UPSELL = "Uploading photos and documents is a Pro feature. Upgrade to aiBLACK Pro to attach photos and documents, identify and edit images, and more.";

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    if (!isPro) { onLimitReached?.(ATTACH_UPSELL); return; }
    handleFiles(files);
  };

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (!isPro) { onLimitReached?.(ATTACH_UPSELL); return; }
    e.preventDefault();
    handleFiles(files);
  };

  const savePhoto = async (idx, image) => {
    setSavingImg(idx);
    try {
      await api.post("/photos/save", { image });
      setSavedImgs((s) => ({ ...s, [idx]: true }));
      toast.success("Saved to your Photos");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save photo");
    } finally {
      setSavingImg("");
    }
  };

  const regenerate = (imgIdx) => {
    if (streaming) return;
    let u = null;
    for (let i = imgIdx - 1; i >= 0; i--) {
      if (messages[i].role === "user") { u = messages[i]; break; }
    }
    if (!u) { toast.error("Couldn't find the original prompt"); return; }
    const reAtts = (u.attachments || []).filter((a) => a.id);
    send(u.content || "Make another variation of this image", reAtts.length ? reAtts : undefined);
  };

  const downloadAnswer = async (idx, content, fmt) => {
    setDlIdx(`${idx}-${fmt}`);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/download`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, filename: "aiBLACK-answer", fmt }),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `aiBLACK-answer.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
    finally { setDlIdx(""); }
  };

  const copyMessage = async (idx, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1800);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const shareMessage = async (text) => {
    const payload = { title: "aiBLACK", text };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copied — ready to share");
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      try { await navigator.clipboard.writeText(text); toast.success("Copied — ready to share"); }
      catch { toast.error("Couldn't share"); }
    }
  };

  const rateMessage = (idx, value) => {
    setRatings((r) => {
      const next = { ...r, [idx]: r[idx] === value ? undefined : value };
      return next;
    });
    if (ratings[idx] !== value) {
      toast.success(value === "up" ? "Thanks for the feedback!" : "Thanks — we'll keep improving");
    }
  };

  const clearResults = () => {
    setMessages([]);
    setRatings({});
    setInput("");
    onNewChat?.();
  };

  useEffect(() => {
    api.get("/prompts").then((r) => setPrompts(r.data.prompts)).catch(() => setPrompts(PROMPTS_FALLBACK));
  }, []);

  useEffect(() => {
    if (skipReloadRef.current) { skipReloadRef.current = false; return; }
    if (conversationId) {
      api.get(`/conversations/${conversationId}`).then((r) => setMessages(r.data.messages)).catch(() => {});
    } else {
      setMessages([]);
    }
  }, [conversationId]);

  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (wasStreamingRef.current && !streaming) {
      const users = scrollRef.current?.querySelectorAll('[data-testid="chat-message-user"]');
      const last = users?.[users.length - 1];
      if (last) last.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    wasStreamingRef.current = streaming;
  }, [messages, streaming]);

  const send = async (text, attsOverride) => {
    const msg = (text ?? input).trim();
    const atts = attsOverride ?? attachments;
    if ((!msg && atts.length === 0) || streaming) return;

    // Free-tier hard limit guard (client-side pre-check)
    if (!isPro && usage && usage.remaining !== null && usage.remaining <= 0) {
      onLimitReached?.("You've used all 10 free chats today. Upgrade to Pro for unlimited conversations.");
      return;
    }

    const attMeta = atts.map((a) => ({ id: a.id, path: a.path, kind: a.kind, filename: a.filename }));
    if (!attsOverride) { setInput(""); setAttachments([]); }
    setMessages((m) => [...m, { role: "user", content: msg, attachments: attMeta }, { role: "assistant", content: "" }]);
    setStreaming(true);
    markActivity();

    try {
      const doFetch = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, conversation_id: conversationId || null,
          attachment_ids: atts.map((a) => a.id) }),
      });
      let res = await doFetch();
      if (res.status === 401) {
        try { await api.post("/auth/refresh"); res = await doFetch(); } catch { /* fall through */ }
      }

      if (res.status === 402) {
        let detail = "You've reached your free daily limit. Upgrade to Pro for unlimited chat.";
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        setMessages((m) => m.slice(0, -2)); // remove optimistic user + placeholder
        onLimitReached?.(detail);
        setStreaming(false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let newConvId = conversationId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5).trim());
          if (data.type === "meta") {
            if (!conversationId && data.conversation_id) {
              newConvId = data.conversation_id;
              skipReloadRef.current = true; // prevent the conversationId effect from clobbering the live stream
              onConversationCreated?.(data.conversation_id);
            }
          } else if (data.type === "sources") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], sources: data.sources };
              return copy;
            });
          } else if (data.type === "image") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant",
                image: data.url, content: data.caption || copy[copy.length - 1].content };
              return copy;
            });
          } else if (data.type === "delta") {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: copy[copy.length - 1].content + data.content };
              return copy;
            });
          } else if (data.type === "error") {
            const isRate = /concurr|limit|429/i.test(data.content || "");
            toast.error(isRate ? "aiBLACK is busy right now — please resend in a moment." : "Something went wrong. Please try again.");
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = {
                ...copy[copy.length - 1],
                role: "assistant",
                content: isRate ? "⚠️ I'm handling a lot of requests right now. Tap send again in a moment." : "⚠️ " + data.content,
              };
              return copy;
            });
          }
        }
      }
      onTitleMaybeChanged?.(newConvId);
      onUsageChanged?.();
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Connection error. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className="flex flex-col h-full relative"
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragActive(false); }}
      onDrop={handleDrop}>
      {dragActive && (
        <div data-testid="chat-drop-overlay"
          className="absolute inset-0 z-[60] flex items-center justify-center bg-[#0a0a0f]/80 backdrop-blur-sm border-2 border-dashed border-[#FF5A00] rounded-2xl pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-[#FF5A00]">
            <ImageDown size={40} />
            <p className="font-display font-bold text-lg text-white">Drop your photo to attach</p>
          </div>
        </div>
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        {empty ? (
          <div className="max-w-2xl mx-auto pt-6 sm:pt-16">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FF5A00] font-bold mb-4">
              <Sparkles size={14} /> The Unfair Advantage
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none mb-3">
              AI Tools and Information
            </h1>
            <p className="text-[#9ca3af] mb-8 max-w-lg">
              Answers with the depth, context, and cultural fluency you deserve. Start with a prompt below or type your own.
            </p>
            <button data-testid="resume-builder-shortcut" onClick={() => navigate("/resume")}
              className="w-full text-left mb-3 flex items-center gap-3 bg-gradient-to-r from-[#FF5A00]/15 to-[#15151d] border border-[#FF5A00]/40 rounded-xl p-4 hover:border-[#FF5A00] transition-colors">
              <div className="w-10 h-10 rounded-lg bg-[#FF5A00] text-[#0a0a0f] flex items-center justify-center shrink-0"><FileText size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white">Resume &amp; Cover Letter Builder</div>
                <div className="text-xs text-[#9ca3af]">Upload your resume, tailor it to any job, download in Word or PDF</div>
              </div>
              <ArrowRight size={18} className="text-[#FF5A00] shrink-0" />
            </button>
            <div className="grid sm:grid-cols-2 gap-3">
              {prompts.map((p, i) => (
                <button key={i} data-testid={`suggested-prompt-${i}`} onClick={() => send(p.text)}
                  className="text-left bg-[#15151d] border border-[#2a3040] rounded-lg p-4 hover:border-[#FF5A00] hover:-translate-y-[2px] transition-all group">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] font-bold mb-1">{p.category}</div>
                  <div className="text-sm text-[#f9fafb] group-hover:text-white">{p.text}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-6">
            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                  data-testid={`chat-message-${m.role}`}>
                  {m.role === "user" ? (
                    <div className="bg-[#1a1a24] text-white border border-[#2a3040] rounded-2xl rounded-tr-sm px-5 py-3 max-w-[85%]" data-testid={`chat-user-bubble-${i}`}>
                      {m.attachments && m.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {m.attachments.map((a, ai) => (
                            a.kind === "image" ? (
                              <img key={ai} src={abs(`/api/files/${a.path}`)} alt={a.filename}
                                data-testid={`chat-attach-image-${i}-${ai}`}
                                className="w-24 h-24 object-cover rounded-lg border border-[#2a3040]" />
                            ) : (
                              <span key={ai} data-testid={`chat-attach-doc-${i}-${ai}`}
                                className="inline-flex items-center gap-1.5 text-xs bg-[#0a0a0f] border border-[#2a3040] rounded-lg px-2.5 py-1.5 max-w-[200px]">
                                <FileText size={13} className="text-[#FF5A00] shrink-0" />
                                <span className="truncate">{a.filename}</span>
                              </span>
                            )
                          ))}
                        </div>
                      )}
                      {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
                    </div>
                  ) : (
                    <div className="border-l-2 border-[#FF5A00] pl-4 py-1 max-w-full">
                      {!m.content && !m.image ? (
                        <div className="flex gap-1.5 py-2">
                          <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                        </div>
                      ) : (
                        <>
                          {m.image && (
                            <>
                              <img src={abs(m.image)} alt={m.content || "Generated image"} data-testid={`chat-image-${i}`}
                                className="rounded-xl border border-[#2a3040] w-full max-w-md mb-2" />
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {typeof m.image === "string" && m.image.startsWith("data:") && (
                                  <button data-testid={`chat-save-photo-${i}`}
                                    onClick={() => savePhoto(i, m.image)}
                                    disabled={savedImgs[i] || savingImg === i}
                                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-sm font-semibold transition-colors ${
                                      savedImgs[i]
                                        ? "bg-[#15151d] text-[#4ade80] border border-[#2a3040] cursor-default"
                                        : "bg-[#FF5A00] text-[#0a0a0f] hover:bg-[#FF7A33]"}`}>
                                    {savingImg === i ? <Loader2 size={14} className="animate-spin" />
                                      : savedImgs[i] ? <Check size={14} /> : <ImageDown size={14} />}
                                    {savedImgs[i] ? "Saved to Photos" : "Save to Photos"}
                                  </button>
                                )}
                                <button data-testid={`chat-regenerate-${i}`}
                                  onClick={() => regenerate(i)} disabled={streaming}
                                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-sm font-semibold bg-[#15151d] text-white border border-[#2a3040] hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors disabled:opacity-50">
                                  <RefreshCw size={14} /> Make another
                                </button>
                              </div>
                            </>
                          )}
                          {m.content && (
                            <div className="chat-md text-[#f9fafb] leading-relaxed"
                              dangerouslySetInnerHTML={renderText(m.content)} />
                          )}
                        </>
                      )}
                      {m.sources && m.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#2a3040]" data-testid={`chat-sources-${i}`}>
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] font-bold mb-2">
                            <Globe size={11} /> Live sources
                          </div>
                          <div className="flex flex-col gap-1.5">
                            {m.sources.map((s, si) => (
                              <a key={si} href={s.url} target="_blank" rel="noreferrer"
                                className="flex items-start gap-2 text-xs text-[#9ca3af] hover:text-[#FF5A00] transition-colors group">
                                <span className="text-[#FF5A00] font-bold shrink-0">[{si + 1}]</span>
                                <span className="truncate group-hover:underline">{s.title}</span>
                                <ExternalLink size={11} className="shrink-0 mt-0.5 opacity-0 group-hover:opacity-100" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {m.content && !(streaming && i === messages.length - 1) && (
                        <div className="flex items-center gap-1 mt-3" data-testid={`chat-actions-${i}`}>
                          <button data-testid={`chat-copy-${i}`} onClick={() => copyMessage(i, m.content)}
                            title="Copy" aria-label="Copy"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
                            {copiedIdx === i ? <Check size={15} className="text-[#4ade80]" /> : <Copy size={15} />}
                          </button>
                          <button data-testid={`chat-share-${i}`} onClick={() => shareMessage(m.content)}
                            title="Share" aria-label="Share"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-[#9ca3af] hover:text-white hover:bg-[#15151d] transition-colors">
                            <Share2 size={15} />
                          </button>
                          <span className="w-px h-4 bg-[#2a3040] mx-1" />
                          <button data-testid={`chat-rate-up-${i}`} onClick={() => rateMessage(i, "up")}
                            title="Good response" aria-label="Good response"
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-[#15151d] ${
                              ratings[i] === "up" ? "text-[#FF5A00]" : "text-[#9ca3af] hover:text-white"}`}>
                            <ThumbsUp size={15} />
                          </button>
                          <button data-testid={`chat-rate-down-${i}`} onClick={() => rateMessage(i, "down")}
                            title="Bad response" aria-label="Bad response"
                            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-[#15151d] ${
                              ratings[i] === "down" ? "text-[#f87171]" : "text-[#9ca3af] hover:text-white"}`}>
                            <ThumbsDown size={15} />
                          </button>
                          {i === messages.length - 1 && !streaming && (
                            <>
                              <span className="w-px h-4 bg-[#2a3040] mx-1" />
                              <button data-testid={`chat-clear-results-${i}`} onClick={clearResults}
                                title="Clear results and start a new chat" aria-label="Clear results"
                                className="flex items-center gap-1.5 h-8 px-2.5 rounded-lg text-sm font-medium text-[#9ca3af] hover:text-[#FF5A00] hover:bg-[#15151d] transition-colors">
                                <Eraser size={14} /> Clear results
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            <div ref={endRef} />
          </div>
        )}
      </div>

      <div className="px-4 sm:px-8 pb-5 pt-2">
        <div className="max-w-3xl mx-auto">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 px-1" data-testid="attach-previews">
              {attachments.map((a) => (
                <div key={a.id} className="relative group">
                  {a.kind === "image" ? (
                    <img src={abs(a.url)} alt={a.filename}
                      className="w-16 h-16 object-cover rounded-lg border border-[#2a3040]" />
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-white bg-[#15151d] border border-[#2a3040] rounded-lg px-2.5 py-2 max-w-[180px]">
                      <FileText size={14} className="text-[#FF5A00] shrink-0" />
                      <span className="truncate">{a.filename}</span>
                    </span>
                  )}
                  <button data-testid={`attach-remove-${a.id}`} onClick={() => removeAttachment(a.id)} aria-label="Remove"
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0a0a0f] border border-[#2a3040] text-[#9ca3af] hover:text-white flex items-center justify-center">
                    <X size={12} />
                  </button>
                </div>
              ))}
              {uploading && <div className="w-16 h-16 rounded-lg border border-[#2a3040] flex items-center justify-center"><Loader2 size={18} className="animate-spin text-[#FF5A00]" /></div>}
            </div>
          )}
          {isPro && attachments.some((a) => a.kind === "image") && (
            <div className="flex flex-wrap gap-2 mb-2 px-1" data-testid="enhance-presets">
              {EDIT_PRESETS.map((p) => (
                <button key={p.label} type="button" data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => send(p.instr)} disabled={streaming || uploading}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#15151d] border border-[#2a3040] rounded-full px-3 py-1.5 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors disabled:opacity-40">
                  <p.icon size={13} className="text-[#FF5A00]" /> {p.label}
                </button>
              ))}
              {attachments.filter((a) => a.kind === "image").length >= 2 && MULTI_PRESETS.map((p) => (
                <button key={p.label} type="button" data-testid={`preset-${p.label.toLowerCase()}`}
                  onClick={() => send(p.instr)} disabled={streaming || uploading}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#15151d] border border-[#2a3040] rounded-full px-3 py-1.5 hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors disabled:opacity-40">
                  <p.icon size={13} className="text-[#FF5A00]" /> {p.label}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="bg-[#15151d] border border-[#FF5A00] rounded-full px-3 sm:px-5 py-2.5 flex items-center gap-2 sm:gap-3 focus-within:ring-1 focus-within:ring-[#FF5A00] transition-colors">
            <input ref={camRef} type="file" accept="image/*" capture="environment"
              style={{ position: "absolute", width: 1, height: 1, left: "-9999px", opacity: 0 }} aria-hidden="true" tabIndex={-1}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
            <input ref={photoRef} type="file" accept="image/*" multiple
              style={{ position: "absolute", width: 1, height: 1, left: "-9999px", opacity: 0 }} aria-hidden="true" tabIndex={-1}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
            <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.txt,.csv,application/pdf"
              style={{ position: "absolute", width: 1, height: 1, left: "-9999px", opacity: 0 }} aria-hidden="true" tabIndex={-1}
              onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
            <div className="relative shrink-0">
              <button type="button" data-testid="attach-button" onClick={() => (isPro ? setAttachOpen((o) => !o) : onLimitReached?.("Uploading photos and documents is a Pro feature. Upgrade to aiBLACK Pro to attach photos and documents, identify and edit images, and more."))} disabled={streaming || uploading}
                aria-label="Attach" title={isPro ? "Attach a photo or document" : "Attach (Pro feature)"}
                className="w-9 h-9 rounded-full flex items-center justify-center text-[#9ca3af] hover:text-white hover:bg-[#0a0a0f] transition-colors disabled:opacity-40">
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={20} />}
              </button>
              {isPro && attachOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setAttachOpen(false)} />
                  <div data-testid="attach-menu"
                    className="absolute bottom-12 left-0 z-50 w-52 bg-[#15151d] border border-[#2a3040] rounded-xl shadow-2xl p-1.5 fade-up">
                    <button type="button" data-testid="attach-camera" onClick={() => { camRef.current?.click(); setAttachOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white hover:bg-[#1a1a24] transition-colors">
                      <Camera size={17} className="text-[#FF5A00]" /> Camera
                    </button>
                    <button type="button" data-testid="attach-photo" onClick={() => { photoRef.current?.click(); setAttachOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white hover:bg-[#1a1a24] transition-colors">
                      <ImageIcon size={17} className="text-[#FF5A00]" /> Photo / Gallery
                    </button>
                    <button type="button" data-testid="attach-document" onClick={() => { docRef.current?.click(); setAttachOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white hover:bg-[#1a1a24] transition-colors">
                      <Paperclip size={17} className="text-[#FF5A00]" /> Document
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="relative flex-1">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onPaste={handlePaste}
                placeholder="" disabled={streaming}
                className="w-full bg-transparent text-white font-semibold focus:outline-none py-1.5" />
              {!input && (
                <span className="pointer-events-none absolute inset-0 flex items-center py-1.5 font-bold text-white">
                  Ask&nbsp;<Brand className="font-black" />&nbsp;anything
                </span>
              )}
            </div>
            <DictateButton disabled={streaming}
              onText={(t) => setInput((prev) => (prev.trim() ? prev.trimEnd() + " " : "") + t)} />
            <button data-testid="chat-send-button" type="submit" disabled={streaming || (!input.trim() && attachments.length === 0)}
              className="shrink-0 w-10 h-10 rounded-full bg-[#FF5A00] text-[#0a0a0f] flex items-center justify-center hover:bg-[#FF7A33] transition-colors disabled:opacity-40">
              <SendHorizontal size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
